/**
 * Session 70 /qa — pure merge logic extracted from chat-view.ts.
 *
 * The chat view embeds three reaction-state transitions in closure-local
 * code (WS REACTION_CHANGED handler, optimistic toggle, server
 * reconciliation). jsdom behavioral tests can't reach them there without
 * a full view-render mock of every collaborator. Extracting the pure
 * transforms here lets the tests exercise exactly the merge semantics
 * that users see, without the DOM + WS + fetch entanglement.
 *
 * Contract for callers (chat-view.ts):
 *   - Pass the CURRENT state.chatMessages (never a stale snapshot).
 *   - Apply the returned array via setState + renderMessages in that order.
 *   - `myId` is the viewer's current `state.playerId` — read at merge
 *     time, not at subscribe time (rejoin resets can change it).
 */

import type { ChatMessage, ReactionTally } from '../store';

/** Wire shape of ReactionToggleResult from the server. Mirrors
 *  `ReactionToggleResultData` in game-api.ts. */
export interface ServerReactionToggleResult {
  messageId: string;
  emoji: string;
  added: boolean;
  reactions: ReactionTally[];
}

/** Wire shape of a WS REACTION_CHANGED frame. All fields are optional on
 *  the network because the envelope is shared with CHAT_MESSAGE. */
export interface ReactionChangedEvent {
  messageId?: string;
  reactions?: ReactionTally[];
  actorPlayerId?: string;
  emoji?: string;
  added?: boolean;
}

/** Mirror of backend `canonicalizeEmoji`. Bare U+2764 → canonical «❤️».
 *  NFC doesn't restore a missing VS16, so we alias explicitly. */
export function canonicalizeEmoji(raw: string): string {
  const nfc = raw.normalize('NFC');
  if (nfc === '\u2764') return '❤️';
  return nfc;
}

/**
 * Apply a WS REACTION_CHANGED event to the current chat-messages list.
 *
 * Key invariants:
 *   1. Only touches the target message (rest pass through unchanged).
 *   2. For the changed emoji: if the actor is THIS viewer, `mine = added`
 *      (the server's echo of our own toggle). If the actor is a teammate,
 *      preserve our prior `mine` state — otherwise someone else reacting
 *      silently flips our own reaction off.
 *   3. For every other emoji in the fresh tally, preserve prior `mine`.
 *      The broadcast carries `mine=false` server-side; patching locally
 *      keeps the viewer's perspective stable.
 *   4. Idempotent: applying the same event twice yields the same result.
 *   5. Emoji canonicalization applies on every comparison so bare «❤»
 *      from a misbehaving client resolves to canonical «❤️».
 *
 * Returns the same reference when the event is malformed or irrelevant
 * (null/undefined fields, no matching message), so callers can cheaply
 * skip setState on no-op events.
 */
export function applyReactionChangedEvent(
  messages: ChatMessage[],
  evt: ReactionChangedEvent,
  myId: string
): ChatMessage[] {
  if (!evt.messageId || !evt.reactions || !evt.emoji) return messages;
  const changedEmoji = canonicalizeEmoji(evt.emoji);
  let touched = false;
  const next = messages.map((m) => {
    if (m.id !== evt.messageId) return m;
    touched = true;
    const prevByEmoji = new Map<string, ReactionTally>();
    (m.reactions ?? []).forEach((r) => prevByEmoji.set(canonicalizeEmoji(r.emoji), r));
    const nextReactions: ReactionTally[] = evt.reactions!.map((r) => {
      const canon = canonicalizeEmoji(r.emoji);
      if (canon === changedEmoji) {
        const actorIsMe = evt.actorPlayerId === myId;
        if (actorIsMe) {
          return { emoji: canon, count: r.count, mine: evt.added === true };
        }
        return { emoji: canon, count: r.count, mine: prevByEmoji.get(canon)?.mine ?? false };
      }
      return { emoji: canon, count: r.count, mine: prevByEmoji.get(canon)?.mine ?? false };
    });
    return { ...m, reactions: nextReactions };
  });
  return touched ? next : messages;
}

/**
 * Compute the optimistic state for a tap-toggle before the server responds.
 *
 * Invariants:
 *   - If the player already has this emoji (row with `mine=true`):
 *       count==1 → row removed entirely from reactions list.
 *       count>1  → count−1, mine=false.
 *   - If the player does NOT have this emoji:
 *       no row yet → append { emoji, count:1, mine:true }.
 *       row with mine=false (others reacted but not us) → count+1, mine=true.
 *
 * The emoji is canonicalized so an external caller passing «❤» still
 * finds the preset row.
 */
export function computeOptimisticToggle(
  messages: ChatMessage[],
  messageId: string,
  emojiRaw: string
): ChatMessage[] {
  const emoji = canonicalizeEmoji(emojiRaw);
  return messages.map((m) => {
    if (m.id !== messageId) return m;
    const existing = [...(m.reactions ?? [])];
    const idx = existing.findIndex((r) => canonicalizeEmoji(r.emoji) === emoji);
    if (idx >= 0) {
      const row = existing[idx];
      if (row.mine) {
        if (row.count <= 1) {
          existing.splice(idx, 1);
        } else {
          existing[idx] = { ...row, count: row.count - 1, mine: false };
        }
      } else {
        existing[idx] = { ...row, count: row.count + 1, mine: true };
      }
    } else {
      existing.push({ emoji, count: 1, mine: true });
    }
    return { ...m, reactions: existing };
  });
}

/**
 * Apply a server-side ReactionToggleResult to the local chat-messages
 * list, reconciling the optimistic state with the server's truth.
 *
 * Same mine-preservation rule as {@link applyReactionChangedEvent} —
 * for the changed emoji, `mine = result.added` (we're the actor); for
 * others, preserve prior `mine`.
 */
export function reconcileReactionToggle(
  messages: ChatMessage[],
  result: ServerReactionToggleResult
): ChatMessage[] {
  const changedEmoji = canonicalizeEmoji(result.emoji);
  return messages.map((m) => {
    if (m.id !== result.messageId) return m;
    const prevByEmoji = new Map<string, ReactionTally>();
    (m.reactions ?? []).forEach((r) => prevByEmoji.set(canonicalizeEmoji(r.emoji), r));
    const next: ReactionTally[] = result.reactions.map((r) => {
      const canon = canonicalizeEmoji(r.emoji);
      if (canon === changedEmoji) {
        return { emoji: canon, count: r.count, mine: result.added };
      }
      return { emoji: canon, count: r.count, mine: prevByEmoji.get(canon)?.mine ?? false };
    });
    return { ...m, reactions: next };
  });
}
